import type { FC } from "react";

import dayjs from "dayjs";
import _ from "lodash";
import numeral from "numeral";
import { compose } from "redux";
import styled from "styled-components";

import Chart from "pro-features/components/Chart/src/Chart";
import { ComparePeriodsLegend } from "pro-features/components/Legends/src/ComparePeriodsLegend/ComparePeriodsLegend";
import { i18nFilter } from "app/filters/i18nFilter";
import { webSourceTextSlimFilter } from "app/filters/ngFilters";
import { generateColumnChartData } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPSingleChart/PoPChartDataGenerator";
import type {
    IPoPGraphProps,
    ISeries,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPSingleChart/PoPChartTypes";
import chartConfig from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPSingleChart/PoPSingleChartConfig";
import { PoPChartContainer } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewPoPChart/PoPSingleChart/StyledComponents";
import { isRemoveSplitPopSingle } from "app/pages/website-analysis/traffic-sources/mmx/components/common/MobileSplit-utils";
import { trafficSources as trafficSourcesDefinitons } from "app/Shared/utils";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const LegendRow = styled(FlexRow)`
    justify-content: space-between;
`;

const defaultXAxisFormatter = ({ value }) => (value ? dayjs.utc(value).utc().format("MMM") : "N/A");

const categoryXAxisFormatter = ({ value }) => value;

const resolveXAxisFormatter = (
    chartDataSeries: ISeries[],
    xAxisFormatter: ({ value }) => string,
    isCategoryXSeries?: boolean,
) => {
    return xAxisFormatter || isCategoryXSeries ? categoryXAxisFormatter : defaultXAxisFormatter;
};

const generateChartData = (chartTitles: string[], chartDataSeries1: ISeries[]) => {
    return generateColumnChartData(chartTitles, chartDataSeries1);
};

const parseSingleModeData = (data, webSource, compareFrom, from, params) => {
    if (webSource == "MobileWeb") {
        data = isRemoveSplitPopSingle(from, compareFrom, data, params);
    }
    const sourceFilter = webSourceTextSlimFilter()(webSource);
    const regularData = _.get(data, ["Data", Object.keys(data.Data)[0]], null);
    const comparedData = _.get(data, ["ComparedData", Object.keys(data.ComparedData)[0]], null);

    if (!regularData || !comparedData) {
        return null;
    }

    // sorting and applying translations for bars
    const channels = _.union(Object.keys(regularData), Object.keys(comparedData))
        .filter((channel) => trafficSourcesDefinitons.hasOwnProperty(channel))
        .sort((ch1, ch2) => {
            return trafficSourcesDefinitons[ch1].priority - trafficSourcesDefinitons[ch2].priority;
        })
        .map((channel) => ({ key: channel, name: translateSourceText(channel) }));

    const allData = channels.reduce((acc, { key, name }) => {
        const regular = regularData[key] ?? 0;
        const compared = comparedData[key] ?? 0;

        acc.push({
            Values: [
                { Key: name, Value: regular },
                { Key: name, Value: compared },
            ],
            Change: compared === 0 ? [NaN] : [regular / compared - 1],
        });

        return acc;
    }, []);

    return {
        [sourceFilter]: allData,
    };
};

const translateSourceText = (source) => {
    return i18nFilter()(trafficSourcesDefinitons[source].title);
};

const defaultYAxisFormatter = ({ value }) =>
    _.isNumber(value) ? numeral(value).format("0[.]0a").toUpperCase() : "N/A";
const defaultOptions: IPoPGraphProps["options"] = { stackedColumn: false, categoryXSeries: false };

export const PoPSingleChart: FC<IPoPGraphProps> = ({
    chartRawData,
    xAxisFormatter,
    yAxisFormatter = defaultYAxisFormatter,
    legendDurations,
    options = defaultOptions,
    showLegend = true,
    postProcessChartData = _.identity,
    postProcessChartConfig = _.identity,
    rightSectionComponent,
    metric,
    webSource,
    globalParams,
}) => {
    const { compareFrom, from, params } = globalParams;
    const type = "column";
    const parsedData = parseSingleModeData(chartRawData, webSource, compareFrom, from, params);
    const titles = parsedData ? Object.keys(parsedData) : null;
    const series1: ISeries[] = parsedData ? parsedData[titles[0]] : [];
    const legendTitles = [titles[0], titles[0]];

    const selectedXAxisFormatter = resolveXAxisFormatter(series1, xAxisFormatter, options?.categoryXSeries);
    const chartData = compose(postProcessChartData, generateChartData)(titles, series1);

    const config = compose(
        postProcessChartConfig,
        chartConfig,
    )({
        type,
        xAxisFormatter: selectedXAxisFormatter,
        yAxisFormatter,
        categoryXSeries: options.categoryXSeries,
        legendDurations,
        height: options.height,
        metric,
    });

    return (
        <PoPChartContainer>
            <LegendRow>
                {legendDurations && showLegend ? (
                    <ComparePeriodsLegend
                        titles={legendTitles}
                        durations={legendDurations}
                    />
                ) : null}
                {rightSectionComponent}
            </LegendRow>
            <div className="chartContainer">
                <Chart
                    type={type}
                    data={chartData}
                    config={config}
                />
            </div>
        </PoPChartContainer>
    );
};

PoPSingleChart.displayName = "PeriodOverPeriodGraph";
